
import * as XLSX from 'xlsx';
import { CreateDistributionCenterPriceInput } from '@/types/database/distribution-center-prices';
import { parseExcelDate } from './utils/dateUtils';
import { getColumnValue } from './utils/columnUtils';

export interface DistributionCenterExcelRow {
  'Timestamp': string | Date;
  'Product List': string;
  'Buying Price': string | number;
  'Location'?: string;
  'Product Remark'?: string;
  // Alternative column names that might be used
  'Date': string | Date;
  'Product Name': string;
  'Price': string | number;
}

export const parseDistributionCenterPricesFromExcel = (workbook: XLSX.WorkBook): {
  data: CreateDistributionCenterPriceInput[];
  errors: string[];
} => {
  const errors: string[] = [];
  const data: CreateDistributionCenterPriceInput[] = [];

  try {
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      errors.push('No sheets found in the Excel file');
      return { data, errors };
    }

    const worksheet = workbook.Sheets[firstSheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
      header: 1,
      defval: null,
      raw: false 
    }) as any[][];

    if (jsonData.length < 2) {
      errors.push('Excel file must contain at least a header row and one data row');
      return { data, errors };
    }

    // Get headers and create objects
    const headers = jsonData[0] as string[];
    const rows = jsonData.slice(1);

    console.log('Excel headers found:', headers);

    rows.forEach((row, index) => {
      const rowIndex = index + 2; // +2 because we skipped header and array is 0-indexed
      
      try {
        // Create row object with headers
        const rowObj: Record<string, any> = {};
        headers.forEach((header, colIndex) => {
          if (header) {
            rowObj[header] = row[colIndex];
          }
        });

        // Skip empty rows
        if (Object.values(rowObj).every(value => !value)) {
          return;
        }

        // Parse date - try both "Timestamp" and "Date" columns
        const dateValue = getColumnValue(rowObj, ['Timestamp', 'Date']);
        const parsedDate = parseExcelDate(dateValue);
        
        if (!parsedDate) {
          errors.push(`Row ${rowIndex}: Invalid or missing date in Timestamp/Date column`);
          return;
        }

        // Parse product name - try both "Product List" and "Product Name" columns
        const productName = getColumnValue(rowObj, ['Product List', 'Product Name']);
        if (!productName || productName.toString().trim() === '') {
          errors.push(`Row ${rowIndex}: Missing product name in Product List/Product Name column`);
          return;
        }

        // Parse price - try both "Buying Price" and "Price" columns
        const priceValue = getColumnValue(rowObj, ['Buying Price', 'Price']);
        let price = 0;
        
        if (priceValue !== null && priceValue !== undefined) {
          const priceStr = priceValue.toString().replace(/[^\d.-]/g, '');
          price = parseFloat(priceStr);
          
          if (isNaN(price)) {
            errors.push(`Row ${rowIndex}: Invalid price value "${priceValue}" in Buying Price/Price column`);
            return;
          }
        }

        // Parse location (optional)
        const location = getColumnValue(rowObj, ['Location']) || 'Unknown';

        // Parse product remark (optional)
        const productRemark = getColumnValue(rowObj, ['Product Remark', 'Remark', 'Notes']);

        const distributionCenterPrice: CreateDistributionCenterPriceInput = {
          date: parsedDate,
          product_name: productName.toString().trim(),
          price: price,
          location: location.toString().trim(),
          product_remark: productRemark ? productRemark.toString().trim() : undefined,
        };

        data.push(distributionCenterPrice);
        
      } catch (error) {
        console.error(`Error parsing row ${rowIndex}:`, error);
        errors.push(`Row ${rowIndex}: ${error instanceof Error ? error.message : 'Unknown parsing error'}`);
      }
    });

  } catch (error) {
    console.error('Error parsing distribution center prices from Excel:', error);
    errors.push(`Failed to parse Excel file: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return { data, errors };
};
